import React, { useEffect, useState } from "react";
import Hoc from "../../../components/HOC";
import Loader from "../../../components/Loader";
import { FaPlus, FaUpload, FaTrash, FaSearch, FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  getAllCandidates,
  updateCandidateStatus,
  getCandidateById,
} from "../../../Services/recruiterapis";
import { useNavigate } from "react-router-dom";
import ModalComponent from "../../../components/Modal";
import CustomDropdown from "../../../components/CustomDropdown";
import {
  MOBILE_LV_LABEL_CLASS,
  MOBILE_LV_VALUE_CLASS,
  MOBILE_LV_CARD_CLASS,
  MOBILE_LV_GRID_CLASS,
  MOBILE_LV_LABEL_LAST,
  MOBILE_LV_VALUE_LAST,
} from "../../../Constants/mobileLabelValueTable";

const DEFAULT_ROWS_PER_PAGE = 10;

const formatPhoneNumber = (phone) => {
  if (!phone || phone === "N/A") return "N/A";
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, "");
  
  // Format as (XXX) XXX-XXXX for 10-digit numbers
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Return original if it doesn't match expected format
  return phone;
};

const statusOptions = [
  { value: "all", label: "All Status" },
  { value: "A", label: "Active" },
  { value: "D", label: "Deactive" },
];

const candidateInitialValues = {
  candidateName: "",
  email: "",
  phone: "",
  technology: "",
  gender: "M",
  notes: "",
  stsCd: "A",
};

const Candidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const [filterValues, setFilterValues] = useState({
    status: statusOptions[0], // Default to "All Status"
  });
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isLoadingCandidate, setIsLoadingCandidate] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const handler = setTimeout(async () => {
      try {
        const params = {
          page: Math.max(currentPage - 1, 0),
          size: rowsPerPage,
          sortBy: "id",
          order: "desc",
        };

        // Add search parameter if provided
        const trimmedSearch = debouncedSearch.trim();
        if (trimmedSearch) {
          params.search = trimmedSearch;
        }

        // Add status filter if not "all"
        const statusValue = filterValues.status?.value;
        if (statusValue && statusValue !== "all") {
          params.stsCd = statusValue;
        }

        const response = await getAllCandidates(params);
        if (!isMounted) return;

        const normalized = (response?.content || []).map((candidate, index) => {
          const fullName = candidate.firstName && candidate.lastName 
            ? `${candidate.firstName} ${candidate.lastName}`.trim()
            : candidate.candidateName || candidate.fullName || candidate.name || "Unnamed Candidate";
          return {
          id: candidate.id ?? candidate.submissionId ?? `candidate-${index}`,
          candidateName: fullName,
          name: fullName,
          email: candidate.email || "N/A",
          phone: candidate.phone || candidate.contactNumber || "N/A",
          technology: candidate.technology || "N/A",
          gender:
            candidate.gender === "M"
              ? "Male"
              : candidate.gender === "F"
              ? "Female"
              : candidate.gender || "N/A",
          notes: candidate.notes || candidate.description || "N/A",
          stsCd: candidate.stsCd || candidate.status || candidate.statusCode || "A",
          updatedAt: candidate.updatedAt || candidate.lastUpdated || "N/A",
          ...candidate,
        };
        });

        setCandidates(normalized);
        setTotalCandidates(response?.totalElements ?? normalized.length ?? 0);
        setTotalPages(response?.totalPages ?? 0);

        const apiTotalPages = response?.totalPages ?? 0;
        if (apiTotalPages > 0 && currentPage > apiTotalPages) {
          setCurrentPage(apiTotalPages);
        } else if (apiTotalPages === 0 && currentPage !== 1 && normalized.length === 0) {
          setCurrentPage(1);
        }
      } catch (error) {
        if (!isMounted) return;
        toast.error(error?.message || "Failed to load candidates");
        setCandidates([]);
        setTotalCandidates(0);
        setTotalPages(0);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }, 400);

    return () => {
      isMounted = false;
      clearTimeout(handler);
    };
  }, [currentPage, rowsPerPage, debouncedSearch, filterValues.status, refreshToken]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterValues.status]);

  const totalPageCount = totalPages || Math.ceil(totalCandidates / rowsPerPage) || 1;

  const handleRowsPerPageChange = (event) => {
    const value = Number(event.target.value) || DEFAULT_ROWS_PER_PAGE;
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleChangePage = (newPage) => {
    if (newPage < 1 || newPage > totalPageCount || newPage === currentPage) return;
    setCurrentPage(newPage);
  };

  const handleAddCandidate = () => {
    navigate("/candidates/add");
  };

  const handleRowClick = async (candidate) => {
    if (!candidate?.id) {
      toast.error("Candidate id missing.");
      return;
    }
    try {
      setIsLoadingCandidate(true);
      const candidateDetails = await getCandidateById(candidate.id);
      setSelectedCandidate(candidateDetails);
      setShowCandidateModal(true);
    } catch (error) {
      toast.error(error?.message || "Failed to load candidate details");
    } finally {
      setIsLoadingCandidate(false);
    }
  };

  const handleCloseCandidateModal = () => {
    setShowCandidateModal(false);
    setSelectedCandidate(null);
  };

  const handleUploadDocuments = (candidate) => {
    if (!candidate?.id) {
      toast.error("Candidate id missing.");
      return;
    }
    const displayName = candidate?.firstName + " " + candidate?.lastName || "N/A";
    navigate(`/candidates/${candidate.id}/documents`, {
      state: { candidateName: displayName || "N/A" },
    });
  };

  const handleEditCandidate = (candidate) => {
    if (!candidate?.id) {
      toast.error("Candidate id missing.");
      return;
    }
    navigate(`/candidates/edit/${candidate.id}`);
  };
  const handleStatusChange = (value) => {
    const newStatus = statusOptions.find((opt) => opt.value === value) || statusOptions[1];
    setFilterValues((prev) => ({
      ...prev,
      status: newStatus,
    }));
  };

  const handleDeleteCandidate = (candidate) => {
    if (!candidate?.id) {
      toast.error("Candidate id missing.");
      return;
    }

    const displayName = candidate?.candidateName || candidate?.name || "this candidate";

    Swal.fire({
      title: "Are you sure?",
      text: `Delete ${displayName}? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, deactivate"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoading(true);
          // await deleteCandidate(candidate.id);
          await updateCandidateStatus(candidate.id);
          toast.success("Candidate deactivated successfully!");
          setRefreshToken((prev) => prev + 1);
          const nextPage = candidates.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
          setCurrentPage(nextPage);
        } catch (error) {
          toast.error(error?.message || "Failed to deactivate candidate");
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const getPageNumbers = () => {
    if (!totalPageCount || totalPageCount <= 0) {
      return [];
    }
    if (totalPageCount <= 5) {
      return Array.from({ length: totalPageCount }, (_, idx) => idx + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5];
    }
    if (currentPage >= totalPageCount - 2) {
      return [
        totalPageCount - 4,
        totalPageCount - 3,
        totalPageCount - 2,
        totalPageCount - 1,
        totalPageCount,
      ];
    }
    return [
      currentPage - 2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      currentPage + 2,
    ];
  };

  const pageNumbers = getPageNumbers();
  const start = totalCandidates === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const end = totalCandidates === 0 ? 0 : Math.min(currentPage * rowsPerPage, totalCandidates);

  return (
      <div className="bg-white">
      <Loader isLoading={isLoading} />

      <div className="p-3 sm:p-6 w-full bg-white min-w-0">
        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-4 py-2 rounded-[4px] border border-[#D1D1D1] bg-white text-sm text-[#201F1E] placeholder-[#656464] focus:outline-none transition-colors duration-150"
                  placeholder="Search candidates by name"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>

              <div className="flex flex-wrap sm:flex-nowrap items-end gap-2 w-full sm:w-auto sm:shrink-0">
                <CustomDropdown
                  value={statusOptions.find((option) => option.value === (filterValues.status?.value || "all"))}
                  name="status"
                  handleChange={(option) => handleStatusChange(option?.value || "all")}
                  placeholder="Status"
                  options={statusOptions}
                  handleGetOptionValue={(opt) => opt.value}
                  handleGetOptionName={(opt) => opt.label}
                  customClasses="w-full min-[480px]:w-40 !mt-0"
                />
                <div className="flex-1 flex justify-end">
                <button
                  type="button"
                  onClick={handleAddCandidate}
                  className="h-[38px] px-4 bg-[#201F1E] hover:bg-[#201F1E] text-white text-sm font-medium rounded-[4px] transition-colors flex items-center justify-center whitespace-nowrap shrink-0"
                >
                  <FaPlus className="mr-2" /> Add Candidate
                </button>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:block overflow-y-auto" style={{ maxHeight: "68vh" }}>
            <table className="min-w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "18%" }}>
                    Candidate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "18%" }}>
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "12%" }}>
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "12%" }}>
                    Technology
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "8%" }}>
                    Gender
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "10%" }}>
                  Experience
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "12%" }}>
                   Visa Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "12%" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {candidates.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                      {isLoading ? "Loading candidates..." : "No candidates found."}
                    </td>
                  </tr>
                ) : (
                  candidates.map((candidate) => (
                    <tr 
                      key={candidate.id} 
                      className="hover:bg-blue-50 cursor-pointer"
                      onClick={() => !isLoadingCandidate && handleRowClick(candidate)}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 truncate" style={{ width: "18%" }}>
                        {candidate.name && candidate.name.length > 20
                          ? `${candidate.name.slice(0, 15)}…`
                          : candidate.firstName + " " + candidate.lastName || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 truncate" style={{ width: "18%" }}>
                        {candidate.email && candidate.email.length > 20
                          ? `${candidate.email.slice(0, 15)}…`
                          : candidate.email || "N/A"}

                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 truncate" style={{ width: "12%" }}>
                        {formatPhoneNumber(candidate.phone)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 truncate" style={{ width: "12%" }}>
                        {candidate.technology && candidate.technology.length > 15
                        
                        ? `${candidate.technology.slice(0, 15)}…`
                        : candidate.technology || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 truncate" style={{ width: "8%" }}>
                        {candidate.gender || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap truncate" style={{ width: "10%" }}>
                        {candidate.totalItExperience || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 truncate" style={{ width: "10%" }}>
                        {candidate.visaStatus || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" style={{ width: "12%" }}>
                        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="text-[#201F1E] hover:text-[#201F1E]"
                            title="Edit candidate"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCandidate(candidate);
                            }}
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            type="button"
                            className="text-green-600 hover:text-green-800"
                            title="Upload documents"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUploadDocuments(candidate);
                            }}
                          >
                            <FaUpload size={16} />
                          </button>
                          <button
                            type="button"
                            className="text-red-600 hover:text-red-800"
                            title="Delete candidate"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCandidate(candidate);
                            }}
                          >
                            <span className="sr-only">Delete</span>
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="md:hidden px-2 pb-2 space-y-4">
            {candidates.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-8">
                {isLoading ? "Loading candidates..." : "No candidates found."}
              </p>
            ) : (
              candidates.map((candidate) => {
                const displayName =
                  candidate.name && candidate.name.length > 20
                    ? `${candidate.name.slice(0, 15)}…`
                    : `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim() || "N/A";
                return (
                  <article
                    key={candidate.id}
                    className={`${MOBILE_LV_CARD_CLASS} cursor-pointer active:opacity-90`}
                    onClick={() => !isLoadingCandidate && handleRowClick(candidate)}
                    role="button"
                  >
                    <div className={MOBILE_LV_GRID_CLASS}>
                      <div className={MOBILE_LV_LABEL_CLASS}>Candidate</div>
                      <div className={MOBILE_LV_VALUE_CLASS}>{displayName}</div>
                      <div className={MOBILE_LV_LABEL_CLASS}>Email</div>
                      <div className={MOBILE_LV_VALUE_CLASS}>{candidate.email || "N/A"}</div>
                      <div className={MOBILE_LV_LABEL_CLASS}>Phone</div>
                      <div className={MOBILE_LV_VALUE_CLASS}>{formatPhoneNumber(candidate.phone)}</div>
                      <div className={MOBILE_LV_LABEL_CLASS}>Technology</div>
                      <div className={MOBILE_LV_VALUE_CLASS}>{candidate.technology || "N/A"}</div>
                      <div className={MOBILE_LV_LABEL_CLASS}>Gender</div>
                      <div className={MOBILE_LV_VALUE_CLASS}>{candidate.gender || "N/A"}</div>
                      <div className={MOBILE_LV_LABEL_CLASS}>Experience</div>
                      <div className={MOBILE_LV_VALUE_CLASS}>{candidate.totalItExperience || "N/A"}</div>
                      <div className={MOBILE_LV_LABEL_CLASS}>Visa Type</div>
                      <div className={MOBILE_LV_VALUE_CLASS}>{candidate.visaStatus || "N/A"}</div>
                      <div className={`${MOBILE_LV_LABEL_CLASS}${MOBILE_LV_LABEL_LAST}`}>Actions</div>
                      <div className={`${MOBILE_LV_VALUE_CLASS}${MOBILE_LV_VALUE_LAST}`}>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            className="text-[#201F1E]"
                            title="Edit candidate"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCandidate(candidate);
                            }}
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            type="button"
                            className="text-green-600"
                            title="Upload documents"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUploadDocuments(candidate);
                            }}
                          >
                            <FaUpload size={16} />
                          </button>
                          <button
                            type="button"
                            className="text-red-600"
                            title="Delete candidate"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCandidate(candidate);
                            }}
                          >
                            <FaTrash size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {[5, 10, 25, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handleChangePage(1)}
                disabled={currentPage === 1 || totalCandidates === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => handleChangePage(currentPage - 1)}
                disabled={currentPage === 1 || totalCandidates === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handleChangePage(currentPage + 1)}
                disabled={currentPage === totalPageCount || totalCandidates === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => handleChangePage(totalPageCount)}
                disabled={currentPage === totalPageCount || totalCandidates === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>

            <div className="hidden sm:flex sm:items-center sm:gap-4">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{start}</span> to{" "}
                <span className="font-medium">{end}</span> of{" "}
                <span className="font-medium">{totalCandidates}</span> candidates
              </p>
              <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handleChangePage(1)}
                  disabled={currentPage === 1 || totalCandidates === 0}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => handleChangePage(currentPage - 1)}
                  disabled={currentPage === 1 || totalCandidates === 0}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {pageNumbers.map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handleChangePage(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 ${
                      pageNum === currentPage ? "bg-blue-50 text-blue-600" : "bg-white text-gray-700"
                    } text-sm font-medium hover:bg-gray-50`}
                  >
                    {pageNum}
                  </button>
                ))}
                <button
                  onClick={() => handleChangePage(currentPage + 1)}
                  disabled={currentPage === totalPageCount || totalCandidates === 0}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => handleChangePage(totalPageCount)}
                  disabled={currentPage === totalPageCount || totalCandidates === 0}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Candidate Information Modal */}
      <ModalComponent
        show={showCandidateModal}
        onClose={handleCloseCandidateModal}
        title="Candidate Information"
        classes="max-w-lg sm:max-w-2xl"
      >
        {isLoadingCandidate ? (
          <div className="flex flex-col justify-center items-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
            <div className="text-gray-500 text-xs">Loading candidate information...</div>
          </div>
        ) : selectedCandidate ? (
          <div className="mt-1">
            <div className="bg-white rounded border border-gray-200">
              <table className="w-full text-xs">
                <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-2 py-1 font-semibold text-gray-700 w-2/5 align-top"> Candidate Name</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.firstName + " " + selectedCandidate.lastName || <span className="text-gray-400 italic">N/A</span>}</td>
                </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Email Address</td>
                    <td className="px-2 py-1 text-gray-900">
                      {selectedCandidate.email ? (
                        <a href={`mailto:${selectedCandidate.email}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                          {selectedCandidate.email}
                        </a>
                      ) : <span className="text-gray-400 italic">N/A</span>}
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">LinkedIn</td>
                    <td className="px-2 py-1 text-gray-900">
                      {selectedCandidate.linkedin ? (
                        <a 
                          href={selectedCandidate.linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                        >
                          <span>{selectedCandidate.linkedin}</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      ) : <span className="text-gray-400 italic">N/A</span>}
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Mobile</td>
                    <td className="px-2 py-1 text-gray-900">
                      {selectedCandidate.phone || <span className="text-gray-400 italic">N/A</span>}
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Gender</td>
                    <td className="px-2 py-1 text-gray-900">
                      {selectedCandidate.gender === "M" ? "MALE" : selectedCandidate.gender === "F" ? "FEMALE" : selectedCandidate.gender || <span className="text-gray-400 italic">N/A</span>}
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Current Location</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.location || <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Nationality</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.nationality ? selectedCandidate.nationality.toUpperCase() : <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Passport Number</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.passportNumber || <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">W2 or C2C</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.workType || <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr>
                 
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Visa Type</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.visaStatus || <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr>
                  {/* <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Visa Expire Date</td>
                    <td className="px-2 py-1 text-gray-900">
                      {selectedCandidate.visaExpiryDate 
                        ? new Date(selectedCandidate.visaExpiryDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                        : <span className="text-gray-400 italic">N/A</span>}
                    </td>
                  </tr> */}
                 
                  {/* <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">DOB (MM/DD)</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.dob || <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr> */}
                  {/* <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top whitespace-nowrap">Former TCS Employee (Y/N - If yes, please provide BA ID)</td>
                    <td className="px-2 py-1 text-gray-900">
                      {selectedCandidate.formerTcsEmployee === "Yes" 
                        ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Yes</span>
                            {selectedCandidate.baId && (
                              <span className="text-gray-700">BA ID: <span className="font-semibold">{selectedCandidate.baId}</span></span>
                            )}
                          </div>
                        )
                        : selectedCandidate.formerTcsEmployee === "No" ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">No</span>
                        ) : <span className="text-gray-400 italic">N/A</span>}
                    </td>
                  </tr> */}
                 
                 <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Technology</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.technology || <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr>
                 
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Total IT Experience</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.totalItExperience || <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr>
                  {/* <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Years of Relevant Experience</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.relevantExperience || <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr> */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Education Details</td>
                    <td className="px-2 py-1 text-gray-900">
                      {[
                        selectedCandidate.highestDegree || null,
                        selectedCandidate.specialization || null,
                        selectedCandidate.educationEnd || null
                      ].filter(Boolean).length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {selectedCandidate.highestDegree && (
                            <span><strong>Degree:</strong> {selectedCandidate.highestDegree}</span>
                          )}
                          {selectedCandidate.specialization && (
                            <span><strong>Specialization:</strong> {selectedCandidate.specialization}</span>
                          )}
                          {selectedCandidate.educationEnd && (
                            <span><strong>Graduation Year:</strong> {selectedCandidate.educationEnd}</span>
                          )}
                        </div>
                      ) : <span className="text-gray-400 italic">N/A</span>}
                    </td>
                  </tr>
                  {/* <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Most Recent Company</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.recentCompany || <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr> */}
                  {/* <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top">Most Recent Company Location</td>
                    <td className="px-2 py-1 text-gray-900">{selectedCandidate.recentCompanyLocation || <span className="text-gray-400 italic">N/A</span>}</td>
                  </tr> */}
                  {/* <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top whitespace-nowrap">Most Recent Employment Start Date</td>
                    <td className="px-2 py-1 text-gray-900">
                      {selectedCandidate.recentEmploymentStart 
                        ? new Date(selectedCandidate.recentEmploymentStart).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
                        : <span className="text-gray-400 italic">N/A</span>}
                    </td>
                  </tr> */}
                  {/* <tr className="hover:bg-gray-50">
                    <td className="px-2 py-1 font-semibold text-gray-700 align-top whitespace-nowrap">Most Recent Employment End Date</td>
                    <td className="px-2 py-1 text-gray-900">
                      {selectedCandidate.recentEmploymentEnd 
                        ? new Date(selectedCandidate.recentEmploymentEnd).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
                        : <span className="text-gray-400 italic">N/A</span>}
                    </td>
                  </tr> */}
                 
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </ModalComponent>
    </div>
  );
};

export default Hoc(Candidates);

